import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'
import PStorage from '@/util/PStorage'
import { UnencryptedContent } from '@/types/crypto'
import { Maybe } from '@/types/general'
import { AppLockedError } from '../AppLockedError'

/**
 * AES(CBC) wrapper functions.
 *
 * Used to encrypt documents (entries)
 */
class AESLib {
    /**
     * Encrypt data using the key specified.
     * As this uses AES-CBC, the result is
     * randomised.
     *
     * @param data unencrypted string
     * @param key AES key
     * @returns encrypted string
     */
    public encrypt(data: string, key: string): string {
        if (!data || data === '') return ''
        return CryptoJS.AES.encrypt(data, key).toString()
    }

    /**
     * Decrypt data that has been encrypted
     * using the AES.encrypt function.
     *
     * @param data encrypted string
     * @param key AES key
     * @returns unencrypted string
     */
    public decrypt(data: string, key: string): string {
        if (!data || data === '') return ''
        return CryptoJS.AES.decrypt(data, key).toString(CryptoJS.enc.Utf8)
    }

    /**
     * Generate a 32-bit key to use when
     * encrypting and decrypting using
     * AES.
     *
     * @returns AES key
     */
    public key(): string {
        return CryptoJS.lib.WordArray.random(32).toString()
    }
}

class RSALib {
    /**
     * Encrypt data using the specified key.
     *
     * @param data unencrypted string
     * @param publicKey RSA public key to encrypt with - falls back
     *                  to stored user public key if missing
     * @returns encrypted string
     */
    public encrypt(data?: string, publicKey?: string): string {
        if (!data || data === '') return ''
        if (!publicKey) publicKey = PStorage.get('public_key')
        const jse = new JSEncrypt()
        jse.setPublicKey(publicKey)
        return jse.encrypt(data).toString()
    }

    /**
     * Decrypt data using the specified
     * private key.
     *
     * @param data encrypted string
     * @param privateKey RSA private key to encrypt with - falls back
     *                   to stored user private key if missing
     * @returns unencryped string
     */
    public decrypt(data: string, privateKey?: string): string {
        if (!data || data === '') return ''
        if (!privateKey) privateKey = PStorage.get('private_key')
        if (!privateKey) throw new AppLockedError('Application locked')
        const jse = new JSEncrypt()
        jse.setPrivateKey(privateKey)
        return jse.decrypt(data).toString()
    }

    /**
     * Generate a key pair for use in
     * RSA functions.
     *
     * @returns RSA publicKey and privateKey
     */
    public keypair(): { publicKey: string, privateKey: string } {
        const jse = new JSEncrypt()
        return {
            publicKey: jse.getPublicKey(),
            privateKey: jse.getPrivateKey()
        }
    }
}

class PCrypto {
    public AES = new AESLib()
    public RSA = new RSALib()

    /**
     * Hash the data using SHA256.
     *
     * @param data
     * @returns hash string
     */
    SHA256(data: string): string {
        return CryptoJS.SHA256(data).toString()
    }

    /**
     * Generate a password-based key using
     * PBKDF2 (password based key derivation
     * function). This will generate consistent
     * keys, so it can be used to mix the user's
     * password and secret key together. The key
     * is used as the salt for the derivation.
     *
     * @param password
     * @param key
     * @returns password-based key
     */
    PBK(password: string, key: string): string {
        return CryptoJS.PBKDF2(password, key).toString()
    }

    /**
     * Generate a cryptographically random secret key.
     *
     * @returns key like 0000-0000-0000-0000
     */
    generateSecretKey(): string {
        const base = this.AES.key()
        return `${base.slice(0, 4)}-${base.slice(4, 8)}-${base.slice(8, 12)}-${base.slice(12, 16)}`
    }

    /**
     * Encrypt an entry's content
     *
     * @param content content from an entry
     * @param encryptedDocKey AES key to encrypt with - if left blank
     *                             a new key will be generated
     * @returns encrypted content + encryptedDocKey
     */
    encryptContent(content: UnencryptedContent, encryptedDocKey?: string) {
        let documentKey = null
        if (!encryptedDocKey) {
            const docKey = this.generateDocumentKey()
            documentKey = docKey.key
            encryptedDocKey = docKey.encryptedKey
        } else {
            const privateKey = PStorage.get('private_key')
            documentKey = this.RSA.decrypt(encryptedDocKey, privateKey)
        }
        return {
            content: this.AES.encrypt(JSON.stringify(content), documentKey),
            key: encryptedDocKey
        }
    }

    /**
     * Decrypt an entry's content
     *
     * @param content encrypted string
     * @param encryptedDocKey AES key to decrypt with
     * @returns original entry content
     */
    decryptContent(encryptedContent: string, encryptedDocKey: string): Maybe<UnencryptedContent> {
        const privateKey = PStorage.get('private_key') as string
        const documentKey = this.RSA.decrypt(encryptedDocKey, privateKey)
        const content = this.AES.decrypt(encryptedContent, documentKey)
        if (content === '') return undefined
        return JSON.parse(content)
    }

    /**
     * Re-encrypt a document key with another user's RSA public key
     *
     * @param encryptedDocKey encrypted string
     * @param publicKey RSA key
     * @returns document key encrypted for the other user
     */
    shareDocumentKey(encryptedDocKey: string, publicKey: string): string {
        const privateKey = PStorage.get('private_key') as string
        const documentKey = this.RSA.decrypt(encryptedDocKey, privateKey)
        return this.RSA.encrypt(documentKey, publicKey)
    }

    /**
     * Generate a new document key
     *
     * @param publicKey RSA public key to encrypt with - if left blank
     *                  the stored user public key will be used
     * @returns key + encryptedKey
     */
    generateDocumentKey(publicKey?: string): { key: string, encryptedKey: string } {
        publicKey = publicKey || PStorage.get('public_key') as string
        const key = this.AES.key()
        return {
            key,
            encryptedKey: this.RSA.encrypt(key, publicKey)
        }
    }
}

export default new PCrypto()
